import { useEffect, useState } from "react";

import { fetchWithAuth } from "~/common/fetch";

const PAGE_SIZE = 100;
const MAX_PAGES = 30;

export type ChatThreadMessage = {
  id: number;
  sender_type: "doctor" | "lab";
  text: string;
  created_at: string | null;
  sender_account_id?: number;
  sender_avatar?: string | null;
  sender_first_name?: string;
  sender_last_name?: string;
  sender_middle_name?: string;
  files: { id: number; file_name: string; mime_type: string | null; get_url: string | null }[];
};

export function useChatThreadAllMessages(threadId: number, enabled: boolean) {
  const [messages, setMessages] = useState<ChatThreadMessage[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setMessages(null);
      setIsLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;

    async function loadAll() {
      setIsLoading(true);
      setError(false);

      try {
        let collected: ChatThreadMessage[] = [];
        let beforeId: number | undefined;
        let pages = 0;

        while (pages < MAX_PAGES) {
          const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
          if (typeof beforeId === "number") {
            params.set("before_id", String(beforeId));
          }

          const response = await fetchWithAuth(
            `${process.env.API}/chat/threads/${threadId}/messages?${params.toString()}`,
          );
          if (!response.ok) {
            throw new Error("Failed to load chat messages");
          }

          const data = (await response.json()) as { messages?: ChatThreadMessage[] };
          const batch = data.messages ?? [];
          if (!batch.length) {
            break;
          }

          collected = [...batch, ...collected];
          beforeId = batch[0]?.id;
          pages += 1;

          if (batch.length < PAGE_SIZE) {
            break;
          }
        }

        if (!cancelled) {
          setMessages(collected);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setMessages([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadAll();

    return () => {
      cancelled = true;
    };
  }, [threadId, enabled]);

  return { messages, isLoading, error };
}
